import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  LinearProgress
} from '@mui/material';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LinkIcon from '@mui/icons-material/Link';

const GOLD = '#d4af37';

interface ViewpointItem {
  id: string;
  name: string;
  distance: string;
  type: string;
  location: string;
  description: string;
  imageUrl?: string;
}

interface ViewpointsContent {
  title: string;
  description: string;
  viewpoints: ViewpointItem[];
}

const ViewpointsNearbyManager = () => {
  const [content, setContent] = useState<ViewpointsContent>({
    title: 'Viewpoints Nearby',
    description: 'Beautiful scenic viewpoints around the area',
    viewpoints: [
      { id: '1', name: 'Scenic Hill View', distance: 'Nearby', type: 'Viewpoint', location: 'Hill Area', description: 'Beautiful scenic viewpoint with panoramic views' },
      { id: '2', name: 'Sunset Point', distance: '2 km', type: 'Beach Viewpoint', location: 'Coastal Area', description: 'Perfect location for sunset viewing' },
    ]
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingId = useRef<string | null>(null);
  const focusHandler = useRef<(() => void) | null>(null);

  const loadContent = useCallback(() => {
    try {
      return onSnapshot(doc(db, 'nearby_content', 'viewpoints'), (docSnap) => {
        if (docSnap.exists()) {
          setContent(docSnap.data() as ViewpointsContent);
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error loading content:', error);
      setLoading(false);
      showSnackbar('Failed to load content', 'error');
      return () => {};
    }
  }, []);

  useEffect(() => {
    const unsubscribe = loadContent();
    return () => unsubscribe();
  }, [loadContent]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'nearby_content', 'viewpoints'), content);
      showSnackbar('Viewpoints data saved successfully', 'success');
    } catch (error) {
      console.error('Error saving:', error);
      showSnackbar('Failed to save content', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAddViewpoint = () => {
    const newViewpoint: ViewpointItem = {
      id: Date.now().toString(),
      name: 'New Viewpoint',
      distance: 'Nearby',
      type: 'Viewpoint',
      location: '',
      description: '',
      imageUrl: '',
    };
    setContent({
      ...content,
      viewpoints: [...content.viewpoints, newViewpoint]
    });
  };

  const handleViewpointChange = (id: string, field: keyof ViewpointItem, value: string) => {
    setContent({
      ...content,
      viewpoints: content.viewpoints.map(viewpoint =>
        viewpoint.id === id ? { ...viewpoint, [field]: value } : viewpoint
      )
    });
  };

  const handleDeleteViewpoint = (id: string) => {
    setContent({
      ...content,
      viewpoints: content.viewpoints.filter(viewpoint => viewpoint.id !== id)
    });
  };

  // ─── Auto-compress image to base64 ───
  const compressImage = (file: File, maxWidth = 1920, maxHeight = 1080, quality = 0.92): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        let { width, height } = img;
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d')!;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        let base64 = canvas.toDataURL('image/jpeg', quality);
        let q = quality;
        while (base64.length > 900_000 && q > 0.5) {
          q -= 0.05;
          base64 = canvas.toDataURL('image/jpeg', q);
        }
        if (base64.length > 900_000) {
          const smallCanvas = document.createElement('canvas');
          const scale = 0.75;
          smallCanvas.width = Math.round(width * scale);
          smallCanvas.height = Math.round(height * scale);
          const sCtx = smallCanvas.getContext('2d')!;
          sCtx.imageSmoothingEnabled = true;
          sCtx.imageSmoothingQuality = 'high';
          sCtx.drawImage(canvas, 0, 0, smallCanvas.width, smallCanvas.height);
          base64 = smallCanvas.toDataURL('image/jpeg', 0.8);
        }
        resolve(base64);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };
      img.src = url;
    });
  };

  // ─── Image Upload Handler ───
  const handleFileUpload = async (id: string, file: File) => {
    if (!file.type.startsWith('image/')) {
      showSnackbar('Please select an image file', 'error');
      return;
    }
    setUploadingId(id);
    setUploadProgress(10);
    try {
      setUploadProgress(30);
      const base64 = await compressImage(file);
      setUploadProgress(90);
      setContent({
        ...content,
        viewpoints: content.viewpoints.map(v =>
          v.id === id ? { ...v, imageUrl: base64 } : v
        )
      });
      setUploadProgress(100);
      const originalKB = Math.round(file.size / 1024);
      const compressedKB = Math.round((base64.length * 3) / 4 / 1024);
      setTimeout(() => {
        setUploadingId(null);
        setUploadProgress(0);
        showSnackbar(`Image compressed (${originalKB}KB → ${compressedKB}KB) & uploaded! Click "Save Changes" to persist.`, 'success');
      }, 400);
    } catch {
      showSnackbar('Failed to process image', 'error');
      setUploadingId(null);
      setUploadProgress(0);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
            Viewpoints Nearby Manager
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Manage nearby viewpoints content
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadContent}
            disabled={loading}
            variant="outlined"
            sx={{ borderColor: GOLD, color: GOLD, '&:hover': { bgcolor: `${GOLD}15` } }}
          >
            Refresh
          </Button>
          <Button
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          🔭 Section Settings
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Section Title"
              value={content.title}
              onChange={(e) => setContent({ ...content, title: e.target.value })}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Section Description"
              value={content.description}
              onChange={(e) => setContent({ ...content, description: e.target.value })}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD }}>
            📍 Viewpoints ({content.viewpoints.length})
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddViewpoint}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            Add Viewpoint
          </Button>
        </Box>

        <Grid container spacing={2}>
          {content.viewpoints.map((viewpoint) => (
            <Grid item xs={12} key={viewpoint.id}>
              <Paper sx={{ p: 2, bgcolor: '#fff', border: `1px solid ${GOLD}` }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Viewpoint Name"
                      value={viewpoint.name}
                      onChange={(e) => handleViewpointChange(viewpoint.id, 'name', e.target.value)}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Type"
                      value={viewpoint.type}
                      onChange={(e) => handleViewpointChange(viewpoint.id, 'type', e.target.value)}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      fullWidth
                      label="Distance"
                      value={viewpoint.distance}
                      onChange={(e) => handleViewpointChange(viewpoint.id, 'distance', e.target.value)}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <IconButton
                      onClick={() => handleDeleteViewpoint(viewpoint.id)}
                      color="error"
                      sx={{ width: '100%' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Location"
                      value={viewpoint.location}
                      onChange={(e) => handleViewpointChange(viewpoint.id, 'location', e.target.value)}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={viewpoint.description}
                      onChange={(e) => handleViewpointChange(viewpoint.id, 'description', e.target.value)}
                      multiline
                      rows={2}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label="Image URL"
                      value={viewpoint.imageUrl?.startsWith('data:') ? '(Uploaded image)' : viewpoint.imageUrl || ''}
                      onChange={(e) => handleViewpointChange(viewpoint.id, 'imageUrl', e.target.value)}
                      size="small"
                      disabled={viewpoint.imageUrl?.startsWith('data:')}
                      placeholder="https://example.com/viewpoint-image.jpg"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <Button
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        disabled={uploadingId === viewpoint.id}
                        onClick={() => {
                          pendingId.current = viewpoint.id;
                          fileInputRef.current?.click();
                          const onFocus = () => {
                            setTimeout(() => {
                              if (pendingId.current === viewpoint.id) {
                                pendingId.current = null;
                              }
                              setUploadingId((prev) => prev === viewpoint.id ? null : prev);
                              setUploadProgress(0);
                            }, 300);
                            window.removeEventListener('focus', onFocus);
                            focusHandler.current = null;
                          };
                          focusHandler.current = onFocus;
                          window.addEventListener('focus', onFocus);
                        }}
                        sx={{
                          borderColor: GOLD, color: GOLD, fontWeight: 600, fontSize: '0.72rem',
                          textTransform: 'none', height: 34,
                          '&:hover': { bgcolor: `${GOLD}15`, borderColor: GOLD },
                        }}
                      >
                        {uploadingId === viewpoint.id ? 'Uploading...' : 'Upload Image'}
                      </Button>
                      {viewpoint.imageUrl?.startsWith('data:') && (
                        <Button
                          size="small"
                          startIcon={<LinkIcon />}
                          onClick={() => handleViewpointChange(viewpoint.id, 'imageUrl', '')}
                          sx={{ textTransform: 'none', fontSize: '0.7rem', color: '#888' }}
                        >
                          Clear & use URL
                        </Button>
                      )}
                    </Box>
                    {uploadingId === viewpoint.id && (
                      <LinearProgress
                        variant="determinate"
                        value={uploadProgress}
                        sx={{ mt: 0.5, borderRadius: 1, '& .MuiLinearProgress-bar': { bgcolor: GOLD } }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    {viewpoint.imageUrl && (
                      <Box sx={{ width: '100%', height: 100, borderRadius: '8px', overflow: 'hidden', border: '1px solid #eee' }}>
                        <img src={viewpoint.imageUrl} alt={viewpoint.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Hidden file input for viewpoint images */}
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={(e) => {
          if (focusHandler.current) {
            window.removeEventListener('focus', focusHandler.current);
            focusHandler.current = null;
          }
          const file = e.target.files?.[0];
          if (file && pendingId.current) {
            handleFileUpload(pendingId.current, file);
          }
          pendingId.current = null;
          e.target.value = '';
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ViewpointsNearbyManager;
